/**
 * Phase 6-C / Journey B — Client Portal "Link expired" page.
 *
 * Shown when:
 *  - The user lands on a route under `/client-portal/*` without a valid token.
 *  - The magic-link `auth` call failed.
 *  - The session expired mid-use.
 *
 * Friendly, brand-neutral (no identity at this point) and reassuring — the fix
 * is always "ask your agency for a fresh link", so we say exactly that.
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import { PORTAL_NEUTRAL } from './brand';

const ClientPortalExpired: React.FC = () => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f6f8',
        px: 2,
      }}
    >
      <Container maxWidth='xs' disableGutters>
        <Paper
          elevation={0}
          variant='outlined'
          sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}
        >
          <Stack spacing={2}>
            <Typography
              variant='h2'
              component='div'
              aria-hidden='true'
              sx={{ fontSize: 40, lineHeight: 1 }}
            >
              🔒
            </Typography>
            <Box>
              <Typography variant='h6' component='h1' sx={{ fontWeight: 700 }}>
                Your link has expired
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                Access links are single-use and expire after a short while —
                that keeps your data secure.
              </Typography>
            </Box>
            <Typography variant='body2' color='text.secondary'>
              Ask your agency contact to resend the access link — or sign in
              with a one-time code below.
            </Typography>
            <Button
              component={RouterLink}
              to='/client-portal/login'
              variant='contained'
              fullWidth
              sx={{
                backgroundColor: PORTAL_NEUTRAL,
                '&:hover': { backgroundColor: '#000' },
                textTransform: 'none',
                fontWeight: 600,
                py: 1.1,
              }}
            >
              Sign in with a code
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default ClientPortalExpired;
